import i18n from '@/i18n';
import { TABLE_FORMATS, UPLOAD_CRS_VALUES, DEFAULT_UPLOAD_CRS, UPLOAD_IN_PROGRESS_STATUSES } from '@/config/upload';
import {
  Upload,
  UploadColumnType,
  UploadGeometryMode,
  UploadPreviewSheet,
  UploadStartColumn,
  UploadStatus,
} from '@/types/upload';

/** One column as the person sets it in the column check. */
export interface ColumnChoice {
  position: number;
  include: boolean;
  name: string;
  type: UploadColumnType;
}

/** What the person picks in the column check of one sheet. */
export interface UploadChoices {
  layerName: string;
  mode: UploadGeometryMode;
  lon: number | null;
  lat: number | null;
  wkt: number | null;
  crs: string;
  columns: ColumnChoice[];
}

export function isTableFormat(upload: Upload): boolean {
  return TABLE_FORMATS.includes(upload.format);
}

/** Whether the import is queued or running, so its status still changes by itself. */
export function isImportInProgress(status: UploadStatus): boolean {
  return UPLOAD_IN_PROGRESS_STATUSES.includes(status);
}

export function hasImportInProgress(uploads: Upload[] | undefined): boolean {
  return uploads?.some((u) => isImportInProgress(u.status)) ?? false;
}

/** The file's name without its extension: the layer's first name. */
export function baseName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '');
}

/**
 * The starting choices for a sheet: the server's suggested coordinate (or WKT) columns, every column in under the
 * name and type the server proposes. A table with no suggested place column starts as data without a map; a file
 * with its own geometry uses it.
 */
export function initialChoices(upload: Upload, sheet: UploadPreviewSheet): UploadChoices {
  const table = isTableFormat(upload);
  const suggested = sheet.suggestedCrs ?? upload.preview?.suggestedCrs;
  const crs = suggested && UPLOAD_CRS_VALUES.includes(suggested) ? suggested : DEFAULT_UPLOAD_CRS;
  let mode: UploadGeometryMode = 'file';
  if (table) {
    if (sheet.suggestedLon != null && sheet.suggestedLat != null) mode = 'lonlat';
    else if (sheet.suggestedWkt != null) mode = 'wkt';
    else mode = 'none';
  }
  return {
    layerName: baseName(upload.fileName) + (sheet.name && (upload.preview?.sheets.length ?? 0) > 1 ? ` - ${sheet.name}` : ''),
    mode,
    lon: sheet.suggestedLon ?? null,
    lat: sheet.suggestedLat ?? null,
    wkt: sheet.suggestedWkt ?? null,
    crs,
    columns: sheet.columns.map((c) => ({ position: c.position, include: true, name: c.suggestedName || c.header, type: c.type })),
  };
}

/** The columns that hold the places: they are read as places, not as fields. */
export function placeColumns(choices: UploadChoices): Set<number> {
  const out = new Set<number>();
  if (choices.mode === 'lonlat') {
    if (choices.lon != null) out.add(choices.lon);
    if (choices.lat != null) out.add(choices.lat);
  }
  if (choices.mode === 'wkt' && choices.wkt != null) out.add(choices.wkt);
  return out;
}

/** Why the choices cannot be started yet (an i18n key under uploads.), or null when they can. */
export function choicesProblem(choices: UploadChoices): string | null {
  if (!choices.layerName.trim()) return 'problem_name';
  if (choices.mode === 'lonlat') {
    if (choices.lon == null || choices.lat == null) return 'problem_both_columns';
    if (choices.lon === choices.lat) return 'problem_same_column';
  }
  if (choices.mode === 'wkt' && choices.wkt == null) return 'problem_wkt_column';
  const places = placeColumns(choices);
  const fields = choices.columns.filter((c) => c.include && !places.has(c.position));
  if (choices.mode === 'none' && fields.length === 0) return 'problem_no_columns';
  const names = fields.map((c) => c.name.trim().toLowerCase());
  if (names.some((n) => !n)) return 'problem_empty_name';
  if (new Set(names).size !== names.length) return 'problem_duplicate_name';
  return null;
}

/** A layer with its own geometry that does not say its coordinate system: the person picks it. */
export function needsCrs(sheet: UploadPreviewSheet): boolean {
  return sheet.crsKnown === false;
}

/** The request for POST /start. */
export function startRequest(sheet: UploadPreviewSheet, choices: UploadChoices) {
  const places = placeColumns(choices);
  const columns: UploadStartColumn[] = choices.columns.map((c) => ({
    position: c.position,
    include: c.include && !places.has(c.position),
    name: c.name.trim(),
    type: c.type,
  }));
  return {
    sheet: sheet.name || null,
    layerName: choices.layerName.trim(),
    geometry: {
      mode: choices.mode,
      lon: choices.mode === 'lonlat' ? choices.lon : null,
      lat: choices.mode === 'lonlat' ? choices.lat : null,
      wkt: choices.mode === 'wkt' ? choices.wkt : null,
      crs: choices.mode === 'lonlat' || choices.mode === 'wkt' || (choices.mode === 'file' && needsCrs(sheet)) ? choices.crs : null,
    },
    columns,
  };
}

/**
 * Every code the server sends - refusals, why an import failed, a row's or column's issue - has a sentence of its own
 * under uploads.code_<code without "upload.">. Keep in step with geonexora-server docs/uploads-api.md.
 */
export const UPLOAD_CODES = [
  // refusals
  'unknown_sheet', 'no_header', 'bad_name', 'bad_geometry_mode', 'bad_column', 'bad_type', 'bad_crs',
  'nothing_to_import', 'bad_request', 'not_waiting', 'importing', 'not_yours', 'busy', 'unsupported_format',
  'wrong_content', 'too_large', 'not_found', 'forbidden',
  // why an import failed
  'too_many_rows', 'empty', 'no_geometry', 'unreadable', 'needs_gdal', 'file_gone', 'interrupted', 'failed',
  // issues of rows and columns
  'row_no_geometry', 'row_bad_coordinate', 'row_bad_geometry', 'row_outside_crs', 'row_bad_value', 'row_refused',
  'column_as_text',
] as const;

/**
 * The codes of shapefiles and other files read through GDAL; their sentences are under uploadShp.code_<code>.
 * Keep in step with geonexora-server docs/uploads-api.md.
 */
export const UPLOAD_GDAL_CODES = [
  'shapefile_needs_zip', 'no_shapefile', 'crs_needed', 'row_made_valid', 'zm_dropped', 'crs_assumed', 'encoding_guessed',
] as const;

/** A server code as a sentence in the person's language, or null when it has none. */
export function codeMessage(code: string | null | undefined): string | null {
  if (!code) return null;
  const name = code.replace(/^upload\./, '');
  if ((UPLOAD_GDAL_CODES as readonly string[]).includes(name)) return i18n.t(`uploadShp.code_${name}`);
  return (UPLOAD_CODES as readonly string[]).includes(name) ? i18n.t(`uploads.code_${name}`) : null;
}

/** The parts of a shapefile, which come in one .zip and are refused on their own. */
export function isLoneShapefilePart(fileName: string): boolean {
  return /\.(shp|shx|dbf|prj|cpg|sbn|sbx|qix|qmd)$/i.test(fileName);
}

/** A refusal as a sentence in the person's language: a known code's own, else the server's message. */
export function uploadErrorMessage(error: unknown): string {
  const e = error as { response?: { status?: number; data?: { code?: string; message?: string; error?: string; detail?: string } }; message?: string };
  const data = e?.response?.data;
  const known = codeMessage(data?.code);
  if (known) return known;
  if (e?.response?.status === 413) return i18n.t('uploads.code_too_large');
  if (e?.response?.status === 403) return i18n.t('uploads.code_forbidden');
  return data?.message || data?.error || data?.detail || e?.message || i18n.t('uploads.failed');
}
